import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Phone, ChevronDown, ChevronUp, HelpCircle, ArrowRight } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const faqCategories = [
  {
    title: "What is Pay Per Call?",
    faqs: [
      { 
        question: "What is Pay Per Call marketing?", 
        answer: "Pay Per Call is a performance-based marketing model where advertisers pay for inbound phone calls from potential customers. Unlike pay-per-click (PPC) where you pay for website visits, Pay Per Call ensures you only pay for actual phone conversations with interested prospects. It's particularly effective for service-based businesses like plumbers, HVAC contractors, roofers, electricians, and other home services providers who rely on phone leads to close deals." 
      },
      { 
        question: "How does Digital Master Media's Pay Per Call network work?", 
        answer: "Our network connects publishers (affiliates who generate calls) with buyers (businesses who want to receive calls). Publishers use various traffic sources like Google Ads, Facebook Ads, SEO, and more to drive potential customers to call tracking numbers. When a qualified call is made and meets the duration requirements, the publisher earns a commission. We handle all tracking, quality assurance, and payments." 
      },
      { 
        question: "What makes Pay Per Call better than other lead generation methods?", 
        answer: "Pay Per Call offers several advantages: 1) Higher intent - people calling are ready to take action, 2) Better conversion rates - phone calls convert 10-15x better than web leads, 3) Immediate connection with customers, 4) No wasted spend on unqualified clicks, 5) Real conversations instead of form fills that may never respond." 
      }
    ]
  },
  {
    title: "For Publishers",
    faqs: [
      { 
        question: "How do publishers generate calls?", 
        answer: "Publishers generate calls through various digital marketing methods including: Google Ads (Search & Display campaigns), Facebook/Meta Ads, Facebook Marketplace listings, SEO-optimized landing pages, Native advertising (Taboola, Outbrain), TikTok Ads, Bing Ads, Email marketing to opt-in lists, and YouTube advertising. The key is creating compelling ad copy and landing pages that encourage users to call." 
      },
      { 
        question: "What are the payment terms for publishers?", 
        answer: "We offer two payment schedules depending on the vertical: Net 7 (Weekly) for Travel and Rental verticals, and Net 10 (Monthly) for all Home Services verticals. The minimum payout threshold is $50. We pay via Payoneer, Wise, and ACH Bank Transfer. All payments are issued from our USA checking account." 
      },
      { 
        question: "How do I become a publisher?", 
        answer: "To become a publisher: 1) Fill out our Publisher Application form, 2) Provide details about your traffic sources and experience, 3) Our team reviews your application within 24-48 hours, 4) Once approved, you'll receive access to our tracking platform, 5) Choose offers and start generating calls!" 
      },
      { 
        question: "What tracking platforms do you support?", 
        answer: "We support all major call tracking platforms including Ringba, Retreaver, CallRail, Invoca, and others. If you don't currently have a tracking platform, we can recommend one based on your needs and volume." 
      }
    ]
  },
  {
    title: "Call Quality & Requirements",
    faqs: [
      { 
        question: "What are the minimum call duration requirements?", 
        answer: "Call duration requirements vary by vertical but typically range from 60 to 120 seconds post-IVR (after the caller completes any automated menu options). Most home service verticals require 120 seconds, while travel and rental verticals require 60 seconds. The buffer time starts after the IVR, not from the beginning of the call." 
      },
      { 
        question: "What is considered a qualified call?", 
        answer: "A qualified call must meet several criteria: 1) Caller has genuine intent for the advertised service, 2) Call meets the minimum duration requirement, 3) Caller is from the target geographic area (USA), 4) Call is not fraudulent or from a bot, 5) Caller engages in meaningful conversation about the service." 
      },
      { 
        question: "What happens to calls that don't meet requirements?", 
        answer: "Calls that don't meet the minimum duration or quality requirements are not billable. We monitor all calls for quality, and any fraudulent or non-compliant calls will not be paid out. Publishers with excessive non-compliant calls may face account review or termination." 
      },
      { 
        question: "How do you ensure call quality?", 
        answer: "We use multiple quality assurance measures: call recording and review, AI-powered fraud detection, duration tracking, geographic verification, duplicate call filtering, and manual review of flagged calls. We also work closely with publishers to optimize their campaigns for higher quality calls." 
      }
    ]
  },
  {
    title: "Traffic Sources & Compliance",
    faqs: [
      { 
        question: "What traffic sources are accepted?", 
        answer: "We accept quality traffic from: Google Ads (Search, Display, Performance Max), Facebook/Meta Ads, Facebook Marketplace, SEO/Organic Traffic, Native Ads (Taboola, Outbrain), TikTok Ads, Bing/Microsoft Ads, YouTube Ads, Display Advertising, and Email Marketing (opt-in lists only). All traffic must be compliant with platform policies." 
      },
      { 
        question: "What traffic sources are NOT allowed?", 
        answer: "We do not accept: Incentivized traffic, fraudulent calls or bot traffic, calls from outside the USA (for USA campaigns), spam or robocalls, traffic from adult or illegal content, calls generated through deceptive practices, and traffic that violates platform advertising policies." 
      },
      { 
        question: "What are the compliance policies?", 
        answer: "All publishers must comply with: FTC advertising guidelines, TCPA (Telephone Consumer Protection Act) regulations, Platform-specific advertising policies (Google, Facebook, etc.), Our network terms of service, and Industry-specific regulations. Violations result in immediate account termination without payment." 
      },
      { 
        question: "What happens if I violate compliance policies?", 
        answer: "Policy violations are taken seriously. Depending on severity: minor violations may result in a warning, repeated violations lead to account suspension, serious violations (fraud, spam, deceptive practices) result in immediate termination with forfeiture of pending payments. We maintain a zero-tolerance policy for fraud." 
      }
    ]
  },
  {
    title: "For Buyers",
    faqs: [
      { 
        question: "How do I start buying calls?", 
        answer: "To become a buyer: 1) Fill out our Buyer Application form with your business details, 2) Our team contacts you within 24-48 hours, 3) We discuss your requirements, target areas, and pricing, 4) Set up your campaign with call routing and tracking, 5) Start receiving qualified inbound calls!" 
      },
      { 
        question: "What verticals do you offer calls for?", 
        answer: "We offer calls across 70+ verticals including: Plumbing, HVAC, Roofing, Electricians, Water & Fire Damage Restoration, Solar Installation, Pest Control, Movers, Garage Doors, Windows, Painters, Bathroom Remodeling, Appliance Repair, Locksmith, Flight Booking, Dumpster Rentals, and many more." 
      },
      { 
        question: "Can I target specific geographic areas?", 
        answer: "Yes! We offer nationwide coverage across all 50 US states. You can target: specific states, specific cities or metro areas, specific zip codes, or nationwide coverage. This allows you to receive calls only from areas you service." 
      },
      { 
        question: "How is pricing determined?", 
        answer: "Pricing varies based on: the vertical (home services typically pay more than rentals), geographic targeting (competitive areas may have higher rates), call quality requirements, volume commitments, and exclusivity arrangements. Contact us for a custom quote based on your specific needs." 
      }
    ]
  },
  {
    title: "Technical & Support",
    faqs: [
      { 
        question: "How do I contact support?", 
        answer: "You can reach our support team through multiple channels: Phone: +1 (855) 471-2511, Email: abidali@digitalmastermedia.com, Telegram: @DigitalMasterMedia. Our business hours are Monday-Friday, 9 AM - 6 PM EST, but we respond to urgent matters as quickly as possible." 
      },
      { 
        question: "What reporting and analytics do you provide?", 
        answer: "Our platform provides: real-time call tracking, detailed call analytics (duration, source, outcome), conversion tracking, revenue and payout reports, historical performance data, and exportable reports. Publishers and buyers both have access to comprehensive dashboards." 
      },
      { 
        question: "Do you provide dedicated account management?", 
        answer: "Yes! Every publisher and buyer receives a dedicated account manager who helps with: campaign optimization, troubleshooting issues, performance analysis, strategy recommendations, and ongoing support. We're invested in your success." 
      }
    ]
  }
];

const FAQItem = ({ question, answer, isOpen, onClick }) => (
  <div className="border border-gray-200 rounded-2xl overflow-hidden transition-all hover:border-blue-300">
    <button onClick={onClick} className="w-full flex items-center justify-between p-5 text-left bg-white hover:bg-gray-50 transition-colors">
      <span className="font-semibold text-gray-900 pr-4">{question}</span>
      {isOpen ? <ChevronUp className="w-5 h-5 text-blue-600 flex-shrink-0" /> : <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />}
    </button>
    {isOpen && <div className="px-5 pb-5 bg-gradient-to-br from-blue-50 to-purple-50"><p className="text-gray-600 whitespace-pre-line">{answer}</p></div>}
  </div>
);

const FAQPage = () => {
  const [openItems, setOpenItems] = useState({});
  const toggleItem = (categoryIndex, faqIndex) => {
    const key = `${categoryIndex}-${faqIndex}`;
    setOpenItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="min-h-screen bg-slate-50 font-['Inter']" data-testid="faq-page">
      <Header />

      {/* Hero */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-[#0f172a] via-[#1e3a5f] to-[#0f172a] overflow-hidden">
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-600/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-purple-600/20 rounded-full blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto px-4 text-center relative">
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500/30 to-purple-500/30 rounded-full flex items-center justify-center mx-auto mb-6 backdrop-blur-sm border border-white/10">
              <HelpCircle className="w-8 h-8 text-blue-400" />
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
              Frequently Asked <span className="gradient-text">Questions</span>
            </h1>
            <p className="text-gray-300 text-lg max-w-2xl mx-auto">
              Everything you need to know about our Pay Per Call network, from getting started to payouts.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          {faqCategories.map((category, categoryIndex) => (
            <motion.div 
              key={categoryIndex} 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ delay: categoryIndex * 0.1 }} 
              className="mb-10"
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <span className="w-8 h-8 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center text-white text-sm font-bold">
                  {categoryIndex + 1}
                </span>
                {category.title}
              </h2>
              <div className="space-y-3">
                {category.faqs.map((faq, faqIndex) => (
                  <FAQItem 
                    key={faqIndex} 
                    question={faq.question} 
                    answer={faq.answer} 
                    isOpen={openItems[`${categoryIndex}-${faqIndex}`]} 
                    onClick={() => toggleItem(categoryIndex, faqIndex)} 
                  />
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-purple-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48Y2lyY2xlIGN4PSIzMCIgY3k9IjMwIiByPSIyIi8+PC9nPjwvZz48L3N2Zz4=')] opacity-30"></div>
        <div className="max-w-4xl mx-auto px-4 text-center relative">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">Still Have Questions?</h2>
          <p className="text-white/90 mb-8">Our team is here to help. Contact us anytime.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 bg-white text-blue-600 font-bold px-8 py-4 rounded-full hover:bg-gray-100 transition-all">
              Contact Us <ArrowRight className="w-5 h-5" />
            </Link>
            <a href="tel:+18554712511" className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-sm text-white font-bold px-8 py-4 rounded-full hover:bg-white/20 border border-white/30 transition-all">
              <Phone className="w-5 h-5" /> +1 (855) 471-2511
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FAQPage;
